import { useEffect, useRef, useState, useCallback } from "react";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

/**
 * Idle-timeout hook.
 *
 * - `timeoutMinutes` : auto-logout if no user activity for this many minutes.
 * - `warnBeforeMinutes` : show a warning modal this many minutes before logout.
 *
 * Returns:
 *   { warning: bool, secondsLeft: number, dismiss: () => void }
 *
 * `onTimeout` is called once when the threshold is hit; `dismiss` resets the
 * timer (used by the "Stay signed in" button).
 */
export function useIdleTimeout({
  timeoutMinutes = 15,
  warnBeforeMinutes = 2,
  onTimeout,
  enabled = true,
} = {}) {
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warnMs = (timeoutMinutes - warnBeforeMinutes) * 60 * 1000;

  const lastActivityRef = useRef(Date.now());
  const tickRef = useRef(null);
  const fired = useRef(false);

  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(warnBeforeMinutes * 60);

  const onActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    fired.current = false;
    if (warning) setWarning(false);
  }, [warning]);

  // Throttled activity listener (1 hz max).
  useEffect(() => {
    if (!enabled) return undefined;
    let lastFire = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastFire < 1000) return;
      lastFire = now;
      onActivity();
    };
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, handler, { passive: true }),
    );
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handler));
    };
  }, [enabled, onActivity]);

  // Tick every second; fire warning + timeout.
  useEffect(() => {
    if (!enabled) return undefined;
    tickRef.current = setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= timeoutMs) {
        if (!fired.current) {
          fired.current = true;
          setWarning(false);
          if (typeof onTimeout === "function") onTimeout();
        }
      } else if (idleFor >= warnMs) {
        const left = Math.max(0, Math.ceil((timeoutMs - idleFor) / 1000));
        setSecondsLeft(left);
        setWarning(true);
      } else if (warning) {
        setWarning(false);
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [enabled, timeoutMs, warnMs, warning, onTimeout]);

  const dismiss = useCallback(() => {
    lastActivityRef.current = Date.now();
    fired.current = false;
    setWarning(false);
  }, []);

  return { warning, secondsLeft, dismiss };
}
